import { useCallback, useState } from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { TextBox } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button, DialogProps, FieldInput, FileUpload, SubmitButton } from 'components'; 
import { useFileUploadValidator, useNavigation } from 'hooks';
import { useInvoiceService } from 'services';
import { useAccountId, useUserInfo } from 'state';
import styles from './UploadInvoiceForm.module.scss';

const PoNumberNotFoundError = 'The PO Number was not found or was in a deleted status. ';
const PoNumberNotFoundAdditionalPopupMessage = 'Please click the "Try Again" button below, review the form, and upload the invoice again. If you continue to experience problems, please contact your Dot Foods representative.';

const isPaidError = 'This purchase order has already been paid. ';
const isPaidAdditionalPopupMessage = 'Please close this pop up and reconfirm payment status of this purchase order. You can also try searching for a partial invoice number under ‘Invoices’ or broaden the time range if filtered. If you continue to experience problems, please contact your Dot Foods representative.';

const getPoNumberMessage = (invalidPoNumber: boolean, isPaid: boolean) => {
    if (invalidPoNumber) return PoNumberNotFoundError;
    if (isPaid) return isPaidError;
    return '';
}

const getErrorBoxMessage = (invalidPoNumber: boolean, isPaid: boolean) => {
    if (invalidPoNumber) return PoNumberNotFoundError + PoNumberNotFoundAdditionalPopupMessage;
    if (isPaid) return isPaidError + isPaidAdditionalPopupMessage;
    return 'Something went wrong.';
}

export default function UploadInvoiceForm() {
    const accountId = useAccountId();
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [invalidPoNumber, setInvalidPoNumber] = useState(false);
    const [isPaid, setIsPaid] = useState(false);
    const invoiceService = useInvoiceService();
    const { fullName, emailAddress } = useUserInfo();
    const fileValidator = useFileUploadValidator(1, 1, ['.pdf']);
    
    const poNumberValidator = useCallback((value: string) => (value
        ? getPoNumberMessage(invalidPoNumber, isPaid)
        : 'Required. Please enter the PO number of the invoice being uploaded.'),
        [invalidPoNumber, isPaid]);

    const handleSubmit = async (dataItem: { [name: string]: any }) => {
        if (submitting) return;
        setSubmitting(true);

        const data = await invoiceService?.getPurchaseOrder(accountId, dataItem.invoicePoNumber);
        if (!data || !data.purchaseOrder) {
            setInvalidPoNumber(true);
            setShowError(true);
            setSubmitting(false);
            return;
        }

        if (data.isPaid) {
            setIsPaid(true);
            setShowError(true);
            setSubmitting(false);
            return;
        }

        const newInvoiceData = {
            poNumber: dataItem.invoicePoNumber,
            accountId: accountId,
            vendorId: data.purchaseOrder.vendorId, 
            userName: fullName,
            userEmail: emailAddress,
        };

        const fileData = dataItem.invoiceFileToUpload?.map((file: any) => file.getRawFile()) ?? [];
        const result = await invoiceService?.uploadInvoice(newInvoiceData, fileData);

        if (result) {
            setShowConfirmation(true);
        } else {
            setShowError(true);
        }            
        setSubmitting(false);
    };

    const onPoNumberChange = () => {
        invalidPoNumber && setInvalidPoNumber(false);
        isPaid && setIsPaid(false);
    };

    return (
        <div className={styles.container}>
            <Form              
                onSubmit={handleSubmit}
                render={({ valid }) => (
                    <FormElement className={styles.form}>   
                        <div className={styles.item}>
                            <label className={styles.itemLabel}
                                htmlFor="invoicePoNumber" 
                                id="invoicePoNumberLabel" 
                            >
                                PO Number
                            </label>
                            <Field
                                component={FieldInput}
                                child={TextBox}
                                className={styles.field}
                                label="Please confirm the PO Number of the invoice to be uploaded."
                                name="invoicePoNumber"
                                ariaLabelledBy="invoicePoNumberLabel" 
                                ariaDescribedBy="invoicePoNumber_label"
                                onChange={onPoNumberChange}
                                required={true}
                                validator={poNumberValidator}
                            />
                        </div>
                        <div className={styles.item}>
                            <label className={styles.itemLabel}
                                htmlFor="invoiceFileToUpload" 
                                id="invoiceFileToUploadLabel" 
                            >
                                Invoice File
                            </label>
                            <Field
                                component={FieldInput}
                                child={FileUpload}
                                className={styles.field}
                                label="Please attach the invoice file to be uploaded (1 file, PDF only)."
                                name="invoiceFileToUpload"
                                ariaLabelledBy="invoiceFileToUploadLabel"
                                ariaDescribedBy="invoiceFileToUpload_label"
                                required={true}
                                validator={fileValidator}
                            />
                        </div>

                        <div className={styles.buttonContainer}>
                            <SubmitButton
                                disabled={!valid}
                                label="UPLOAD INVOICE"
                                submittingLabel="UPLOADING"
                                isSubmitting={submitting}                    
                                themeColor="primary"
                            />
                        </div>

                        {showConfirmation && <ConfirmationDialog onClose={() => setShowConfirmation(false)} />}
                        {showError && <ErrorDialog invalidPoNumber={invalidPoNumber} isPaid={isPaid} onClose={() => setShowError(false)} />}

                    </FormElement>
                )}
            />
        </div>
    );
}

const ConfirmationDialog = ({ onClose }: DialogProps) => {
    const { navigateToInvoices } = useNavigation();

    return (
        <Dialog 
            className={styles.dialog}
            autoFocus={true}
            onClose={onClose}
        >
            <div className={styles.content}>
                <div className={styles.title}>
                    Thank You
                </div>
                <div className={styles.message}>
                    Your invoice has been received. Please click the OK button below to continue.  
                </div>
            </div>
            <DialogActionsBar>
                <div className={styles.button}>
                    <Button onClick={navigateToInvoices}>
                        OK
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

type InvoiceErrorDialogProps = {
    invalidPoNumber: boolean;
    isPaid: boolean;
} & DialogProps;

const ErrorDialog = ({ invalidPoNumber, isPaid, onClose }: InvoiceErrorDialogProps) => (
    <Dialog
        className={styles.dialog}
        autoFocus={true}
        onClose={onClose}                
    > 
        <div className={styles.content}>
            <div className={styles.title}>
                Oops!
            </div>
            <div className={styles.message}>
                {getErrorBoxMessage(invalidPoNumber, isPaid)}
            </div>
        </div>
        <DialogActionsBar>
            <div className={styles.button}>
                <Button
                    onClick={onClose}
                >
                    Try Again
                </Button>
            </div>
        </DialogActionsBar>
    </Dialog>
);